import { useState, useEffect } from 'react'
import classNames from 'classnames'
import QuantityBar from '../../../../components/quantity-bar/quantity-bar'
import useUpdateStorePartHandler from '../../effects/update-store-part'
import { formatCurrency } from '../../../helpers/utils'
import CurrencyField from 'components/CurrencyField/currencyField'
import { EuiFieldNumber, EuiFieldText, EuiFormRow, EuiButton } from '@equipmentshare/ds2'

import styles from '../../index.module.css'
import { StorePart } from '../../../../types/storePart'

export type StorePartFormData = {
	threshold: number | '',
	max: number | '',
	cost: string,
	location: string,
	note: string,
}

export type StorePartFormValidation = {
	threshold: { isInvalid: boolean, error: string },
	max: { isInvalid: boolean, error: string },
}


function initialFormData( { threshold, max, cost, location, note }: StorePart ): StorePartFormData {
	return {
		threshold: threshold ?? '',
		max: max ?? '',
		cost: cost ? cost.toString() : '',
		location: location ?? '',
		note: note ?? '',
	}
}

export type EditStorePartProps = {
	storePart: StorePart,
	onClose: () => void,
	onSubmit: () => void,
	onSuccess: () => void,
}

export function getNumericFieldToSubmit( value?: number | '' ) {
	if ( value === undefined || value === '' ) {
		return null
	}

	return value
}

export default function EditStorePart( { storePart, onClose, onSubmit, onSuccess }: EditStorePartProps ) {
	const submitEditStorePart = useUpdateStorePartHandler()
	const [ formData, setFormData ] = useState( initialFormData( storePart ) )
	const [ formValidation, setFormValidation ] = useState<StorePartFormValidation>( {
		threshold: { isInvalid: false, error: '' },
		max: { isInvalid: false, error: '' },
	} )
	const [ isSaving, setIsSaving ] = useState( false )

	const handleInputChange = ( field: string ) => ( event: React.ChangeEvent<HTMLInputElement> ) => {
		const value = event.currentTarget.value

		setFormData( {
			...formData,
			[ field ]: value,
		} )
	}

	const handleIntegerChange = ( field: keyof typeof formData ) => ( event: React.ChangeEvent<HTMLInputElement> ) => {
		if ( ( field === 'threshold' || field === 'max' ) && formValidation[ field ]?.isInvalid ) {
			setFormValidation( {
				...formValidation,
				[ field ]: { isInvalid: false, error: '' }
			} )
		}

		const intValue = parseInt( event.currentTarget.value.replace( /\D/g, '' ), 10 )
		const fieldValue = isNaN( intValue ) ? '' : intValue

		setFormData( {
			...formData,
			[ field ]: fieldValue,
		} )
	}

	const handleBlur = ( field: keyof typeof formData ) => () => {
		const value = formData[ field ]
		const v = { isInvalid: false, error: '' }

		if ( value === '' && storePart[ field ] !== undefined && storePart[ field ] !== null ) {
			v.isInvalid = true
			v.error = 'Please set a value. If you do not want to use this field, set it to 0.'
		}

		setFormValidation( {
			...formValidation,
			[ field ]: v,
		} )
	}

	const handleCostChange = ( value: string|number ) => {
		setFormData( {
			...formData,
			cost: value as string,
		} )
	}

	const handleSubmit = () => {
		setIsSaving( true )
		onSubmit()
		submitEditStorePart( {
			storePartId: storePart.id,
			cost: getNumericFieldToSubmit( formData.cost === '' ? formData.cost : parseFloat( formData.cost ) ),
			threshold: getNumericFieldToSubmit( formData.threshold ),
			max: getNumericFieldToSubmit( formData.max ),
			location: formData.location,
			note: formData.note,
		} ).then( onSuccess )
	}

	useEffect( () => setFormData( initialFormData( storePart ) ), [ storePart ] )

	const isInvalid = formValidation.threshold.isInvalid || formValidation.max.isInvalid

	return (
		<div>
			<div className={styles.group}>
				<div className={styles.row}>
					<EuiFormRow
						label="Location"
					>
						<EuiFieldText
							id="location"
							value={formData.location}
							onChange={handleInputChange( 'location' )}
						/>
					</EuiFormRow>
				</div>
			</div>

			<div className={styles.quantityGroup}>
				<div className={styles.row}>
					Quantity in store {storePart.inventoryLocation.name}
				</div>

				<div className={classNames( styles.quantityBarContainer, styles.largerText )}>
					<div className={styles.qty}>{storePart.availableQuantity}</div>
					<QuantityBar
						max={formData.max}
						threshold={formData.threshold}
						total={storePart.availableQuantity}
					/>
				</div>

				<div className={styles.row}>
					<EuiFormRow
						label="Minimum"
						{...formValidation.threshold}
					>
						<EuiFieldNumber
							id="threshold"
							value={formData.threshold}
							onChange={handleIntegerChange( 'threshold' )}
							onBlur={handleBlur( 'threshold' )}
							isInvalid={formValidation.threshold.isInvalid}
						/>
					</EuiFormRow>
				</div>
				<div className={styles.row}>
					<EuiFormRow
						label="Maximum"
						{...formValidation.max}
					>
						<EuiFieldNumber
							id="max"
							value={formData.max}
							onChange={handleIntegerChange( 'max' )}
							onBlur={handleBlur( 'max' )}
							isInvalid={formValidation.max.isInvalid}
						/>
					</EuiFormRow>
				</div>
			</div>

			<div className={styles.group}>
				<div className={styles.row}>
					<EuiFormRow
						label="Cost per unit"
					>
						<CurrencyField
							id="cost"
							onRawChange={handleCostChange}
							value={formData.cost}
						/>
					</EuiFormRow>
				</div>
				<div className={styles.row}>
					Total value in inventory: <span className={styles.value}>{formatCurrency( ( ( typeof formData.cost === 'number' ? formData.cost : ( formData.cost === '' ? 0 : parseFloat( formData.cost ) ) ) * storePart.availableQuantity ).toString() )}</span>
				</div>
			</div>

			<div className={styles.group}>
				<div className={styles.row}>
					<EuiFormRow
						label="Note"
					>
						<EuiFieldText
							id="note"
							value={formData.note}
							onChange={handleInputChange( 'note' )}
						/>
					</EuiFormRow>
				</div>
			</div>

			<div className={styles.buttonContainer}>
				<EuiButton
					color="text"
					id="cancel"
					onClick={onClose}
				>
          Cancel
				</EuiButton>
				<EuiButton
					fill
					id="submit"
					color="primary"
					onClick={handleSubmit}
					isLoading={isSaving}
					isDisabled={isInvalid}
				>
          Submit
				</EuiButton>
			</div>
		</div>
	)
}
